import React, { useEffect, useRef } from 'react';
import { useQuery } from 'react-query';

import { Form } from 'antd';
import styled from 'styled-components';

import { getCommunityFileCategories } from 'apis/CommunityAPI';
import { useRootContext } from 'components/layout/RootContext';

import { analyzeDocumentFiles, clearDocumentAnalysisErrors, normalizeDocuments } from './helpers';
import ShopDataModalDocumentsUploadThumbnail from './ShopDataModalDocumentsUploadThumbnail';
import ShopDataModalDraggerField from './ShopDataModalDraggerField';
import { FILE_TYPES } from '../../../common/Table/constants';
import { FILE_SOURCE } from '../../../constants';
import ShopDataModalContentHeader from '../common/ShopDataModalContentHeader';
import { FIELD_CATEGORY } from '../constants';
import { useShopDataModalContext } from '../ShopDataModalContext';

const DraggerFieldWrapper = styled.div`
  padding: 20px 15px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const ShopDataModalDocuments = () => {
  const { showMessage } = useRootContext();
  const { currentData, form, disabled, setCurrentData, handleDataStatusChanged, communityId } =
    useShopDataModalContext();
  const analyzingUids = useRef(new Set<string>());

  const { data: categories = [] } = useQuery({
    queryKey: ['shop-data', 'files', 'categories'],
    queryFn: getCommunityFileCategories,
    enabled: communityId > 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const categoryOptions = (categories ?? []).map((item: any) => ({
    label: item.name,
    value: item.id,
  }));

  const setDocumentsState = (documents: any[]) => {
    const normalizedDocuments = normalizeDocuments(documents);

    form?.setFieldValue([FIELD_CATEGORY.DOCUMENTS], normalizedDocuments);
    setCurrentData((prevState: any) => ({
      ...prevState,
      [FIELD_CATEGORY.DOCUMENTS]: normalizedDocuments,
    }));
  };

  const analyzeFiles = async (files: any[]) => {
    await analyzeDocumentFiles({
      files,
      communityId,
      source: `${FILE_SOURCE.MEDIA}`,
      analyzingUids: analyzingUids.current,
      getDocuments: () => form?.getFieldValue([FIELD_CATEGORY.DOCUMENTS]) ?? [],
      setDocuments: setDocumentsState,
      showMessage,
    });
  };

  const onValuesChange = async (values: any, allValues: any) => {
    handleDataStatusChanged([FIELD_CATEGORY.DOCUMENTS]);
    const documents = clearDocumentAnalysisErrors(allValues[FIELD_CATEGORY.DOCUMENTS] ?? []);
    setCurrentData((prevState: any) => ({
      ...prevState,
      [FIELD_CATEGORY.DOCUMENTS]: documents,
    }));
    await analyzeFiles(documents);
  };

  useEffect(() => {
    form?.setFieldValue([FIELD_CATEGORY.DOCUMENTS], currentData?.[FIELD_CATEGORY.DOCUMENTS] ?? []);
  }, [currentData, form]);

  return (
    <Container>
      <ShopDataModalContentHeader title="Brochures & Documents" />
      <Form form={form} disabled={disabled} onValuesChange={onValuesChange}>
        <DraggerFieldWrapper>
          <ShopDataModalDraggerField
            name={[FIELD_CATEGORY.DOCUMENTS]}
            label="files"
            disabled={disabled}
            multiple
            accept={FILE_TYPES.join(',')}
            fileList={currentData?.[FIELD_CATEGORY.DOCUMENTS] ?? []}
            itemRender={(originNode: React.ReactNode, file: any, fileList: any, actions: any) =>
              ShopDataModalDocumentsUploadThumbnail(originNode, file, fileList, actions, categoryOptions, disabled)
            }
            dataTestId="sd-modal-brochures-input"
          />
        </DraggerFieldWrapper>
      </Form>
    </Container>
  );
};

export default ShopDataModalDocuments;
